import {
  // CheckCircleIcon,
  ChevronLeftIcon,
  XIcon,
} from '@heroicons/react/outline';
import { Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import TickOnboarding from 'assets/Icons/TickOnboarding';
import moment from 'moment';
import { usePathname } from 'next/navigation';
import React, { memo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';
import { getScreenName } from '@/hooks/usePageTracking';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { LoadingV2 } from '@/components/shared/LoadingV2';
import { notify } from '@/components/shared/notify';

import { getSubPlanName, getSubPlanNameLog } from '@/pages/gems-transactions';

// import BestValueBadge from '../../BestValueBadge';
// import LimitedTimeBadge from '../../LimitedTimeButton';
import { user } from '../../../../atoms/user';
import { subscriptionCancel } from '../../../../services/Subscribe';
import {
  IPlan,
  PeriodicityTypeEnum,
} from '../../../../utils/interfaces/Subscription';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  plan?: IPlan;
}
const AutoRenewalModal: React.FC<Props> = ({ open, setOpen, plan }) => {
  const pathname = usePathname();

  const [loading, setLoading] = useState<boolean>(false);
  const [polling, setPolling] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  const [userData, setUserData] = useRecoilState(user);

  const screens = useBreakpoint();
  const physical = plan?.features[0]?.periodicity && plan.rules[0]?.physical;

  const subscriptionCancelHandle = async () => {
    try {
      setLoading(true);
      await subscriptionCancel();
      notify('Auto-Renewal Canceled', 'success');
      setOpen(false);
      setUserData((prev) =>
        prev ? { ...prev, subscription_canceled: true } : undefined,
      );
      const eventParams: EventDetails = {
        event_name: 'subscription_cancelled',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digital',
        source_id: userData?.subscription.id.toString(),
        sub_source: 'plan',
        sub_source_id: userData?.subscription.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(plan?.name ?? ''),
        unit: 'day',
        value:
          plan?.periodicity_type === PeriodicityTypeEnum.daily
            ? plan.periodicity
            : plan?.periodicity_type === PeriodicityTypeEnum.monthly
              ? plan.periodicity * 30
              : plan?.periodicity_type === PeriodicityTypeEnum.yearly
                ? plan.periodicity * 365
                : '',
        meta: {
          click_source: getScreenName(pathname),
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: pathname,
      });
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const subscriptionHandle = () => {
    subscriptionCancelHandle();
  };

  // const physical = plan?.features[0]?.periodicity && plan.rules[0]?.physical;

  // const digital = plan?.features[0]?.periodicity && plan.rules[0]?.digital;

  // const bookPerMonth = plan?.features[0]?.periodicity
  //   ? plan?.features?.[0]?.quota * plan?.periodicity
  //   : null;

  return (
    <Modal
      title={null}
      closable={false}
      open={open}
      onCancel={() => {
        setOpen(false);
        setPolling(false);
        setSubscriptionSuccess(false);
      }}
      centered
      footer={null}
      className='m-0'
      classNames={{
        content: 'p-0 rounded-2xl md:w-[500px] w-full',
        wrapper: 'md:text-center text-start',
        body: 'md:w-auto w-dvw',
      }}
    >
      {polling ? (
        <div className='relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <LoadingV2
            loaderColor='white'
            messages={[
              'Preparing your subscription benefits...',
              'Activating your premium access...',
              'Setting up your book deliveries...',
              'Finalizing your subscription...',
              'Unlocking exclusive content for you...',
              'Almost ready to dive into your books!',
              'You’re all set—just a moment more!',
            ]}
          />
        </div>
      ) : subscriptionSuccess ? (
        <div className='relative md:block flex flex-col items-center justify-center  border md:border-white/10 border-black md:rounded-2xl md:px-6 md:py-20 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <div className='absolute top-0 right-0 m-5'>
            <ButtonDefault
              onClick={() => {
                setOpen(false);
                setPolling(false);
                setSubscriptionSuccess(false);
              }}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <XIcon className='w-6 h-6 text-white' />
            </ButtonDefault>
          </div>
          <div className=' w-full flex-1 flex flex-col justify-center'>
            <div className='flex justify-center md:pb-8'>
              <TickOnboarding width={screens.md ? '100' : '64'} />
            </div>
            <span className='text-xl md:text-2xl xl:text-3xl  text-start flex justify-center md:text-center font-semibold text-white'>
              Congratulations{' '}
              <p className='text-xl md:text-3xl xl:text-4xl inline-flex pl-2 -translate-y-0.5 md:-translate-y-2'>
                🎉
              </p>
            </span>

            <div className='text-white/70 text-xs flex  justify-center md:text-lg  lg:text-center w-full '>
              Subscription completed successfully.
            </div>
          </div>
        </div>
      ) : (
        <div className='md:block flex flex-col  border md:border-white/10 border-black md:rounded-2xl md:p-6 p-0 md:bg-grayZl-950 bg-black md:h-auto h-dvh'>
          <div className='py-[11px] pl-2 pr-3 md:hidden flex items-center gap-3'>
            <ButtonDefault
              onClick={() => setOpen(false)}
              variant={ButtonVariants.UNSTYLED}
              size={0}
              roundedUnstyled
            >
              <ChevronLeftIcon className='w-[22px] h-[22px] text-white' />
            </ButtonDefault>
            {/* <p className='text-sx leading-[22px] font-medium text-white'>
              Subscription
            </p> */}
          </div>
          <div className='flex-1 overflow-auto md:mb-8 mb-0'>
            <div className='md:flex hidden items-start justify-end mb-6 '>
              <ButtonDefault
                onClick={() => {
                  setOpen(false);
                  setPolling(false);
                  setSubscriptionSuccess(false);
                }}
                variant={ButtonVariants.UNSTYLED}
                size={0}
                roundedUnstyled
              >
                <XIcon className='w-6 h-6 text-white' />
              </ButtonDefault>
            </div>
            <div className='flex flex-col md:gap-2 gap-1 md:items-center md:mb-8 mb-6 md:mx-16 mx-4'>
              <p className='md:text-center text-white md:text-[32px] text-lg font-semibold '>
                Cancel Auto-Renewal
              </p>
              <p className='md:text-center text-grayZl-500 md:text-lg text-sm'>
                Your plan stays active until it expires, even if you cancel
                auto-renewal.
              </p>
            </div>
            <div className='rounded-lg border border-grayZl-850 md:mx-8 mx-4 overflow-hidden'>
              <div className='px-4 py-2 bg-dropDownMenuBg'>
                <p className='text-white/40 uppercase md:text-xs text-[10px]'>
                  Your Subscription Details
                </p>
              </div>
              <div className='md:px-4 md:py-6 p-4 flex flex-col md:gap-3 gap-2'>
                <div className='flex flex-col gap-1'>
                  {plan ? (
                    <p className='text-primary text-xs md:text-sm'>
                      {plan.periodicity}
                      {plan.periodicity_type === 'monthly' ? ' Month' : ''} Plan
                    </p>
                  ) : null}

                  {plan ? (
                    <p className='text-white md:text-2xl text-lg font-semibold'>
                      {getSubPlanName(plan?.name)}
                    </p>
                  ) : null}
                </div>
                {plan?.razorpay_plan.amount ? (
                  <div className='md:text-lg text-sm  text-grayZl-600'>
                    Your next bill is for&nbsp;
                    {'₹' + plan?.razorpay_plan.amount / 100}&nbsp;on&nbsp;
                    {moment(userData?.subscription?.expired_at).format(
                      'DD/MM/YY',
                    )}
                    .
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className='flex items-center gap-3 md:pb-6 md:px-6 px-4 md:p-0 pb-4 pt-4  md:border-none border-t-[0.5px] border-grayZl-850'>
            <ButtonDefault
              disabled={loading}
              loading={loading}
              variant={ButtonVariants.PRIMARY}
              size={0}
              className='flex flex-1 truncate px-6 py-[9px] rounded-lg'
              onClick={subscriptionHandle}
              roundedUnstyled
            >
              {/* {loading ? (
              <Loading3QuartersOutlined className='animate-spin w-5 h-5' />
            ) : null} */}
              Cancel Auto-Renewal
            </ButtonDefault>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default memo(AutoRenewalModal);
